import MultiSelect, { ISelectOption } from '@widgets/multi-select';
import CustomRangeDatePicker from '@widgets/range-date-picker-with-modes';
import { useManagerOptions } from '@app/queries/options/useManagerOptions';
import { FC, useEffect, useMemo, useState } from 'react';
import { useSourceOptions } from '@app/queries/options/useSourceOptions';
import { getDateShortcuts, parseDDMMYYYY, subtractDaysFromDate, toIsoDate } from '@app/utils/date';
import { useSelector } from 'react-redux';
import { selectLastUploadedReportAt } from '@app/redux/features/userSlice';
import IconResetFilters from '@shared/icons/IconResetFilters';
import { useClientOptions } from '@app/queries/options/useClientOptions';
import { useHostingOptions } from '@app/queries/options/useHostingOptions';
import { useClientCategoryOptions } from '@app/queries/options/useClientCategoryOptions';
import { useHostingWholesaleOptions } from '@app/queries/options/useHostingWholesaleOptions';

export interface IHostingIndicatorsFilter {
  dateFrom: string;
  dateTo: string;
  managers: string[];
  sources: string[];
  clients: string[];
  client_categories: string[];
  hosting: string[];
  wholesale: string[];
}

const DefaultFilter: IHostingIndicatorsFilter = {
  dateFrom: '',
  dateTo: '',
  managers: [],
  sources: [],
  clients: [],
  client_categories: [],
  hosting: [],
  wholesale: [],
};

interface IProps {
  onChangeFilters: (values: IHostingIndicatorsFilter) => void;
  filters: IHostingIndicatorsFilter;
}

const MAX_DATE = subtractDaysFromDate(new Date(), 1);

const HostingIndicatorsFilter: FC<IProps> = ({ onChangeFilters, filters }) => {
  const managerOptionsFetcher = useManagerOptions();
  const sourceOptionsFetcher = useSourceOptions();
  const clientFilterFetcher = useClientOptions();
  const clientCategoryFilterFetcher = useClientCategoryOptions();
  const wholesaleFilterFetcher = useHostingWholesaleOptions();
  const hostingFilterFetcher = useHostingOptions();

  const reportLastSyncDate = useSelector(selectLastUploadedReportAt);

  const [managerOptions, setManagerOptions] = useState<ISelectOption[]>();
  const [sourceOptions, setSourceOptions] = useState<ISelectOption[]>();
  const [clientOptions, setClientOptions] = useState<ISelectOption[]>();
  const [clientCategoryOptions, setClientCategoryOptions] = useState<ISelectOption[]>();
  const [wholesaleOptions, setWholesaleOptions] = useState<ISelectOption[]>();
  const [hostingOptions, setHostingOptions] = useState<ISelectOption[]>();

  useEffect(() => {
    (async () => {
      const data = await managerOptionsFetcher.fetchData();
      setManagerOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await sourceOptionsFetcher.fetchData();
      setSourceOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await clientFilterFetcher.fetchData();
      setClientOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await hostingFilterFetcher.fetch();
      setHostingOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await clientCategoryFilterFetcher.fetch();
      setClientCategoryOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await wholesaleFilterFetcher.fetch();
      setWholesaleOptions(data);
    })();
  }, []);

  const customDateShortcuts = useMemo(() => {
    if (!reportLastSyncDate) return;

    const parsedReportLastSyncDate = parseDDMMYYYY(reportLastSyncDate);
    if (Number.isNaN(end.getTime())) return undefined;

    return {
      ...getDateShortcuts(parsedReportLastSyncDate),
      all: {
        text: 'Все',
        period: {
          start: new Date(new Date().setTime(1639699202000)),
          end: new Date(),
        },
      },
    };
  }, [reportLastSyncDate]);

  return (
    <div className="flex gap-[10px] mb-[15px] items-center">
      <div className="cursor-pointer" onClick={() => onChangeFilters({ ...DefaultFilter })}>
        <IconResetFilters />
      </div>
      <CustomRangeDatePicker
        onChange={values =>
          onChangeFilters({
            ...filters,
            dateFrom: values.startDate,
            dateTo: values.endDate,
          })
        }
        width={180}
        shortcuts={customDateShortcuts}
        maxDate={
          reportLastSyncDate
            ? new Date(reportLastSyncDate.split('.').reverse().join('-'))
            : MAX_DATE
        }
        value={{ startDate: filters?.dateFrom, endDate: filters?.dateTo }}
      />
      <MultiSelect
        title="Менеджер"
        value={
          managerOptions?.length
            ? managerOptions?.filter(item => filters?.managers?.includes(item.value))
            : []
        }
        width={100}
        isLoading={!managerOptionsFetcher.isSuccess}
        options={managerOptions}
        onChange={values =>
          onChangeFilters({ ...filters, managers: values.map(item => item.value) })
        }
      />
      <MultiSelect
        value={
          hostingOptions?.length
            ? hostingOptions?.filter(item => filters?.hosting?.includes(item.value))
            : []
        }
        isLoading={!hostingFilterFetcher.isSuccess}
        title="Хостинг"
        options={hostingOptions}
        width={110}
        onChange={values =>
          onChangeFilters({ ...filters, hosting: values.map(item => item.value) })
        }
      />
      <MultiSelect
        value={
          sourceOptions?.length
            ? sourceOptions?.filter(item => filters?.sources?.includes(item.value))
            : []
        }
        isLoading={!sourceOptionsFetcher.isSuccess}
        title="Источник"
        options={sourceOptions}
        width={110}
        onChange={values =>
          onChangeFilters({ ...filters, sources: values.map(item => item.value) })
        }
      />
      <MultiSelect
        isLoading={!clientFilterFetcher.isSuccess}
        value={
          clientOptions?.length
            ? clientOptions?.filter(item => filters?.clients?.includes(item.value))
            : []
        }
        title="Клиент"
        width={110}
        options={clientOptions}
        onChange={values =>
          onChangeFilters({ ...filters, clients: values.map(item => item.value) })
        }
      />
      <MultiSelect
        value={
          clientCategoryOptions?.length
            ? clientCategoryOptions?.filter(item =>
                filters?.client_categories?.includes(item.value)
              )
            : []
        }
        isLoading={!clientCategoryFilterFetcher.isSuccess}
        title="Категория клиента"
        options={clientCategoryOptions}
        width={150}
        onChange={values =>
          onChangeFilters({ ...filters, client_categories: values.map(item => item.value) })
        }
      />
      <MultiSelect
        value={
          wholesaleOptions?.length
            ? wholesaleOptions?.filter(item => filters?.wholesale?.includes(item.value))
            : []
        }
        isLoading={!wholesaleFilterFetcher.isSuccess}
        title="Опт"
        options={wholesaleOptions}
        width={70}
        onChange={values =>
          onChangeFilters({ ...filters, wholesale: values.map(item => item.value) })
        }
      />
    </div>
  );
};

export default HostingIndicatorsFilter;
